import {
  Create,
  CreateBase,
  Edit,
  EditBase,
  FormDataConsumer,
  SaveButton,
  SimpleForm,
  Toolbar,
  TopToolbar,
  useNotify,
  useResourceContext,
  useResourceDefinition,
} from "react-admin";
import { errorHandlingFactory } from "../utils/errors";
import { Settings } from "../utils/settings";
import { Debug } from "./debug";
import { Card, CardContent } from "@mui/material";
import { JsonStyled } from "./jsonStyled";
import { useLocation } from "react-router-dom";
import { Row } from "./row";
import { asArray, splitArray } from "../utils/tools";
import { Column } from "./column";
import { GoBackButton } from "./goBackButton";
import { RecordTitle } from "./recordTitle";
import { MyShowButton as ShowButton } from "./myShowButton";
import { WithPermissions } from "../acl/withPermissions";
import { GrafanaButton } from "./grafanaButton";
import { DebugDotJson } from "./debugDotJson";
import { onlyExistentFields } from "../acl/existentFields";
import { useRenderCounter } from "./renderCounter";
import { MyContextProvider, useMyContext } from "./myContext";
import set from "lodash/set";

const MutatorFormData = () => {
  return (
    <FormDataConsumer>
      {({ formData }) => (
        <DebugDotJson
          data={formData}
          jsonProps={{ wrapperStyle: { maxHeight: "60vh" } }}
        />
      )}
    </FormDataConsumer>
  );
};

const MutatorToolbar = ({ sx, saveEnabled, ...props }) => {
  return (
    <Toolbar sx={{ justifyContent: "space-between", ...sx }} {...props}>
      <SaveButton disabled={!saveEnabled} />
      <GoBackButton size="medium" variant="contained" />
    </Toolbar>
  );
};

const MutatorEditActions = () => (
  <TopToolbar>
    <ShowButton />
    <Debug>
      <GrafanaButton
        expr={` |= \`PUT /backend/v1/${useResourceDefinition().name}\` `}
      />
    </Debug>
  </TopToolbar>
);

const MutatorCreateActions = () => (
  <TopToolbar>
    <Debug>
      <GrafanaButton
        expr={` |= \`POST /backend/v1/${useResourceDefinition().name}\` `}
      />
    </Debug>
  </TopToolbar>
);

export const MyEdit = (props) => <MyMutator {...props} mutatorType="edit" />;
export const MyCreate = (props) => (
  <MyMutator {...props} mutatorType="create" />
);

const isEmptyValue = (value) => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

const removeEmptyFields = (data) => {
  if (Array.isArray(data)) {
    const arr = data.map(removeEmptyFields).filter((v) => !isEmptyValue(v));
    return arr;
  }
  if (data && typeof data === "object") {
    const result = {};
    Object.entries(data).forEach(([key, value]) => {
      const cleaned = removeEmptyFields(value);
      if (!isEmptyValue(cleaned)) {
        result[key] = cleaned;
      }
    });
    return result;
  }
  return data;
};

const MyMutator = ({
  saveEnabled,
  labelField,
  formProps,
  children,
  columns,
  actions,
  permissionAction,
  permissionResource,
  forcedCustomFields = [],
  removedCustomFields = [],
  header = null,
  footer = null,
  ignoreEmptyFields,
  customSegment,
  idfield,
  ...props
}) => {
  const location = useLocation();
  const redirectTo = location.state?.redirectTo;

  if (props.redirect == MyMutator.defaultProps.redirect && redirectTo) {
    props.redirect = redirectTo;
  }

  const action = permissionAction || props.mutatorType || "create";
  const resource = permissionResource || useResourceContext();
  const debugLabel = `MyMutator-${resource}-${action}`;

  useRenderCounter(`${action} ${resource}`);

  actions =
    actions ||
    (props.mutatorType == "edit" ? (
      <MutatorEditActions />
    ) : (
      <MutatorCreateActions />
    ));
  const Mutator = props.mutatorType == "edit" ? Edit : Create;
  const MutatorBase = props.mutatorType == "edit" ? EditBase : CreateBase;

  const notify = useNotify();
  const { acp } = useMyContext();
  const onError = errorHandlingFactory(notify, acp?.isSuperuser());

  const processedProps = Object.assign(
    {
      mutationMode: Settings.getValue("pessimisticSave")
        ? "pessimistic"
        : "optimistic",
    },
    props
  );

  formProps = {
    warnWhenUnsavedChanges: true,
    toolbar: <MutatorToolbar saveEnabled={saveEnabled} />,
    ...formProps,
  };

  const mutationOptions = { onError, ...props.mutationOptions };
  let {
    className,
    sx,
    style,
    transform: pre_transform,
    ...mutatorProps
  } = processedProps;

  mutatorProps = { ...mutatorProps };

  if (customSegment) {
    set(mutatorProps, "queryOptions.meta.customSegment", customSegment);
    set(mutationOptions, "meta.customSegment", customSegment);
  }

  if (idfield) {
    set(mutatorProps, "queryOptions.meta.transformResponse", (response) => ({
      ...response,
      __id: response.id,
      id: response[idfield],
    }));
    set(mutationOptions, "meta.transformResponse", (response) => ({
      ...response,
      __id: response.id,
      id: response[idfield],
    }));
  }

  if (mutationOptions?.meta?.customSegment && !permissionAction) {
    console.warn(`${debugLabel}: customSegment set, permissionAction not set`);
  }

  let transform = null;
  if (ignoreEmptyFields) {
    if (pre_transform) {
      transform = (data) => {
        const pre_transformed = pre_transform(data);
        const clean = removeEmptyFields(pre_transformed);
        return clean;
      };
    } else {
      transform = removeEmptyFields;
    }
  } else {
    transform = pre_transform;
  }

  if (children && !Array.isArray(children)) {
    children = [children];
  }

  children = onlyExistentFields(
    children,
    resource,
    action,
    forcedCustomFields,
    removedCustomFields,
    "request_fields"
  );

  const sources = asArray(children).map((child) => child?.props?.source);

  const column_groups = splitArray(children, columns);
  children = (
    <>
      {header}
      <Row>
        {column_groups.map((column, columnIdx) => (
          <Column key={columnIdx}>{column.map((field) => field)}</Column>
        ))}
        <MutatorFormData />
      </Row>
      {footer}
    </>
  );

  return (
    <WithPermissions can={{ resource, action }} label={debugLabel}>
      <MyContextProvider value={{ mutatorSources: sources }}>
        <Column
          className={"MutatorWrapper " + (className || "")}
          sx={sx}
          style={style}
        >
          <MutatorBase {...mutatorProps}>
            <RecordTitle labelField={labelField} actions={actions} />
          </MutatorBase>
          <Mutator
            {...mutatorProps}
            transform={transform}
            actions={null}
            mutationOptions={mutationOptions}
          >
            <SimpleForm {...formProps}>{children}</SimpleForm>
          </Mutator>
          <Debug foldable={true}>
            <Card>
              <CardContent>
                <h3>Processed Props</h3>
                <JsonStyled data={mutatorProps} />
                <h3>Location Info</h3>
                <JsonStyled data={useLocation()} />
                <h3>Sources</h3>
                <JsonStyled data={sources} />
              </CardContent>
            </Card>
          </Debug>
        </Column>
      </MyContextProvider>
    </WithPermissions>
  );
};

MyMutator.defaultProps = {
  redirect: -1,
  mutatorType: "edit",
  columns: 2,
  saveEnabled: true,
};
